import { generatePath } from '@toggle/helpers';
import { conversations, SearchConversationResponse } from '@toggle/toggle';

import { apiFetch, wretchRequest } from '~/utils/api-fetch/apiFetch';

export const FETCH_ALL_CONVERSATIONS_PAGE_SIZE = 50;

export type SubmittedAnalysisMode = 'Deep' | 'Auto';
export type WebSearch = 'restricted' | 'open';

type BasePostConversationArgs = {
  message: string;
  analysis_mode?: SubmittedAnalysisMode;
  tool_config?: {
    modes: {
      web_search?: WebSearch;
    };
  };
  attachments?: string[];
  controller?: AbortController;
};
type PostContinueConversationArgs = BasePostConversationArgs & {
  conversationId: string;
};

export const postConversation = async ({
  message,
  analysis_mode,
  tool_config,
  attachments,
  controller,
}: BasePostConversationArgs) => {
  return apiFetch(conversations.postConversation.path, {
    schema: conversations.postConversation.schema,
    method: 'post',
    body: {
      message,
      analysis_mode,
      tool_config,
      attachments,
    },
    signal: controller?.signal,
  });
};

export const postContinueConversation = async ({
  message,
  conversationId,
  analysis_mode,
  controller,
  tool_config,
  attachments,
}: PostContinueConversationArgs) => {
  return apiFetch(conversations.continueConversation.path(conversationId), {
    schema: conversations.continueConversation.schema,
    method: 'post',
    body: {
      message,
      analysis_mode,
      tool_config,
      attachments,
    },
    signal: controller?.signal,
  });
};

export const getConversationTasks = async (id: string, signal?: AbortSignal) =>
  apiFetch(conversations.getConversationTask.path(id), {
    schema: conversations.getConversationTask.schema,
    signal,
  });

export const searchConversation = (query: string, page: number) => {
  const requestPath = generatePath({
    basePath: conversations.searchConversation.path,
    params: {
      q: query,
      page,
    },
  });

  return wretchRequest<SearchConversationResponse>(requestPath, {
    schema: conversations.searchConversation.schema,
  });
};

type GetConversationTaskStatesArgs = {
  conversationId: string;
  taskRequestId: string;
};

export const getConversationTaskStates = ({
  conversationId,
  taskRequestId,
}: GetConversationTaskStatesArgs) => {
  return apiFetch(
    conversations.getConversationMessage.path(conversationId, taskRequestId),
    {
      schema: conversations.getConversationMessage.schema,
    }
  );
};

export const getConversationMessageWithSteps = ({
  messageId,
  conversationId,
  controller,
}: {
  messageId: string;
  conversationId: string;
  controller?: AbortController;
}) => {
  return wretchRequest(
    conversations.getConversationMessage.path(conversationId, messageId),
    {
      schema: conversations.getConversationMessage.schema,
      signal: controller?.signal,
    }
  );
};

export const updateConversation = ({
  id,
  name,
  favourite,
}: {
  id: string;
  name?: string;
  favourite?: boolean;
}) => {
  const body = favourite !== undefined ? { name, favourite } : { name };
  return apiFetch(conversations.conversationId.path(id), {
    method: 'put',
    body,
  });
};

export const deleteConversation = (id: string) =>
  apiFetch(conversations.conversationId.path(id), {
    method: 'delete',
  });

export const getAllConversations = (page: number) => {
  const requestPath = generatePath({
    basePath: conversations.getAllConversations.path,
    params: {
      page,
      page_size: FETCH_ALL_CONVERSATIONS_PAGE_SIZE,
    },
  });

  return wretchRequest(requestPath, {
    schema: conversations.getAllConversations.schema,
  });
};

export const postFeedback = (body: {
  conversation_id: string;
  request_id: string;
  feedback: string;
}) => {
  wretchRequest(conversations.feedback.path, {
    method: 'post',
    body,
  });
};

export const abortRequest = (id: string) =>
  apiFetch(conversations.abortConversation.path(id), {
    method: 'put',
    body: { action: 'cancel' },
  });

export const copyConversation = (id: string, name: string) =>
  apiFetch(conversations.changeAccessLevel.path(id), {
    method: 'post',
    schema: conversations.changeAccessLevel.schema,
    body: {
      access_level: 'public',
      name,
    },
  });

export const postClonePublicConversation = (id: string) =>
  apiFetch(conversations.changeAccessLevel.path(id), {
    method: 'post',
    schema: conversations.changeAccessLevel.schema,
    body: {
      access_level: 'private',
    },
  });
